import React, { useEffect, useMemo, useState } from "react";
import { formatDate, getMonthMatrix, isSameDay } from "../utils/dateUtils";
import { deleteCalendarEventById, subscribeCalendarEventsByRange } from "../services/calendarService";
import type { CalendarEvent } from "../types/calendar";
import { useToast } from "../components/Toast";
import { useAuth } from "../context/AuthContext";
import { CreateCalendarEventModal } from "../components/calendar/CreateCalendarEventModal";

export function CalendarPage({
  onNavigate,
}: {
  onNavigate: (key: string, id: string) => void;
}) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const canDeleteAnyEvent = useMemo(() => {
    const email = (user?.email || "").toLowerCase();
    return email === "kc@lg.app" || email === "ad@lg.app";
  }, [user?.email]);

  const [monthCursor, setMonthCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [openCreate, setOpenCreate] = useState(false);

  useEffect(() => {
    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth();
    const start = new Date(y, m, 1, 0, 0, 0, 0);
    const end = new Date(y, m + 1, 0, 23, 59, 59, 999);

    // buffer for calendar grid
    start.setDate(start.getDate() - 14);
    end.setDate(end.getDate() + 14);
    end.setHours(23, 59, 59, 999);

    const unsubscribe = subscribeCalendarEventsByRange(start, end, setEvents);
    return () => unsubscribe();
  }, [monthCursor]);

  const matrix = useMemo(
    () => getMonthMatrix(monthCursor.getFullYear(), monthCursor.getMonth()),
    [monthCursor]
  );

  const getEventColor = (e: CalendarEvent) => {
    if (e.color) return e.color;
    if (e.autoType === "contract-sign-date") return "#10b981";
    if (e.autoType === "contract-expiry-0") return "#ef4444";
    if (e.autoType === "contract-expiry-30" || e.autoType === "contract-expiry-60")
      return "#f59e0b";
    if (e.autoType === "contract-review-deadline") return "#f97316";
    if (e.sourceType === "manual") return "#666";
    return "#444";
  };

  const getSafeTimeStr = (isoStr?: string) => {
    if (!isoStr || typeof isoStr !== "string") return "";
    const parts = isoStr.split("T");
    if (parts.length < 2) return "";
    return parts[1].slice(0, 5);
  };

  const getEventsForDay = (day: Date) => {
    const dayStr = formatDate(day);
    return events.filter((e) => {
      // TIME_RANGE: compare by Date
      if (e.mode === "TIME_RANGE" || !e.startDate) {
        return isSameDay(e.date || new Date(0), day);
      }
      // ALL_DAY_RANGE: compare by YYYY-MM-DD range
      if (e.startDate && e.endDate) {
        return dayStr >= e.startDate && dayStr <= e.endDate;
      }
      return false;
    });
  };

  const selectedEvents = useMemo(() => {
    const list = getEventsForDay(selectedDate);
    return [...list].sort((a, b) => {
      const aTime = a.mode === "TIME_RANGE" ? a.startDateTime || "" : "99:99";
      const bTime = b.mode === "TIME_RANGE" ? b.startDateTime || "" : "99:99";
      return aTime.localeCompare(bTime);
    });
  }, [selectedDate, events]);

  const handleDelete = async (ev: CalendarEvent) => {
    const isAuto = ev.sourceType !== "manual";
    if (isAuto && !canDeleteAnyEvent) {
      alert("此事件為系統自動產生，僅 kc / ad 可直接刪除；其他使用者請回來源資料修改。");
      return;
    }

    if (!confirm("確定刪除此事件？")) return;

    try {
      const originalId = (ev as any).originalId as string | undefined;
      const targetId = originalId || ev.id;
      await deleteCalendarEventById(targetId);
      showToast("已刪除");
    } catch {
      showToast("刪除失敗", "error");
    }
  };

  const handleEventClick = (ev: CalendarEvent) => {
    const map: Record<string, string> = {
      contract: "contracts",
      contractReview: "contractReviews",
      case: "legalMatters",
      "legal-matter": "legalMatters",
      "ip-matter": "ipMatters",
      brandAsset: "brandAssets",
    };
    const key = map[ev.sourceType];
    if (key && ev.sourceId) onNavigate(key, ev.sourceId);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        className="row"
        style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}
      >
        <button
          className="smallbtn"
          onClick={() => {
            const t = new Date();
            setMonthCursor(new Date(t.getFullYear(), t.getMonth(), 1));
            setSelectedDate(t);
          }}
        >
          今天
        </button>

        <div className="row" style={{ alignItems: "center", gap: 12 }}>
          <button
            className="smallbtn"
            onClick={() =>
              setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))
            }
          >
            &lt;
          </button>
          <div style={{ fontWeight: "bold", fontSize: 18, minWidth: 120, textAlign: "center" }}>
            {monthCursor.getFullYear()}年 {monthCursor.getMonth() + 1}月
          </div>
          <button
            className="smallbtn"
            onClick={() =>
              setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))
            }
          >
            &gt;
          </button>
        </div>

        <button
          className="smallbtn"
          style={{ background: "#fff", color: "#000", fontWeight: "bold" }}
          onClick={() => setOpenCreate(true)}
        >
          + 新增事件
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 320px",
          gap: 12,
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Grid */}
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            {[
              "日",
              "一",
              "二",
              "三",
              "四",
              "五",
              "六",
            ].map((d, i) => (
              <div key={d} style={{ color: i === 0 || i === 6 ? "#fbbf24" : "var(--muted)" }}>
                {d}
              </div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 4,
              flex: 1,
              overflow: "hidden",
            }}
          >
            {matrix.flat().map((cell) => {
              const dayEvents = getEventsForDay(cell.date);
              const isSelected = isSameDay(selectedDate, cell.date);

              return (
                <div
                  key={cell.dateStr}
                  onClick={() => setSelectedDate(cell.date)}
                  style={{
                    background: isSelected ? "rgba(59, 130, 246, 0.2)" : "#222",
                    border: isSelected ? "1px solid #3b82f6" : "1px solid #333",
                    borderRadius: 6,
                    padding: 4,
                    opacity: cell.isCurrentMonth ? 1 : 0.4,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: 12,
                      color: cell.isToday ? "var(--ok)" : cell.isHoliday ? "#fbbf24" : "#aaa",
                      fontWeight: cell.isToday ? "bold" : "normal",
                    }}
                  >
                    {cell.day}
                  </div>

                  <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2 }}>
                    {dayEvents.slice(0, 3).map((ev) => {
                      const timeLabel =
                        ev.mode === "TIME_RANGE" ? `${getSafeTimeStr(ev.startDateTime)} ` : "";
                      return (
                        <div
                          key={ev.id}
                          title={ev.title}
                          style={{
                            fontSize: 10,
                            background: getEventColor(ev),
                            color: "#fff",
                            borderRadius: 2,
                            padding: "1px 3px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {timeLabel}
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div style={{ fontSize: 10, color: "#aaa", textAlign: "center" }}>
                        +{dayEvents.length - 3}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Side list */}
        <div
          style={{
            border: "1px solid #333",
            borderRadius: 8,
            padding: 12,
            overflow: "auto",
            background: "#1b1b1b",
          }}
        >
          <div style={{ fontWeight: "bold", fontSize: 16 }}>{formatDate(selectedDate)}</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
            {selectedEvents.length} 筆行程
          </div>

          <div style={{ marginTop: 12 }}>
            {selectedEvents.length === 0 && (
              <div style={{ color: "#666", textAlign: "center", marginTop: 20 }}>無行程</div>
            )}

            {selectedEvents.map((ev) => {
              const timeLabel =
                ev.mode === "TIME_RANGE"
                  ? `${getSafeTimeStr(ev.startDateTime)} - ${getSafeTimeStr(ev.endDateTime)}`
                  : "全天";
              const canDelete = ev.sourceType === "manual" || canDeleteAnyEvent;

              return (
                <div
                  key={(ev as any).originalId ? `${(ev as any).originalId}-${ev.id}` : ev.id}
                  style={{
                    border: "1px solid #2b2b2b",
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 10,
                    background: "#111",
                  }}
                >
                  <div className="row" style={{ alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 4 }}>
                        {timeLabel}{" "}
                        {ev.sourceType !== "manual" ? "· 系統事件" : ""}
                      </div>
                      <div
                        onClick={() => handleEventClick(ev)}
                        title={ev.sourceId ? "點擊跳轉來源" : undefined}
                        style={{
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: ev.sourceId ? "pointer" : "default",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ev.title}
                      </div>
                      {ev.note && (
                        <div style={{ marginTop: 6, fontSize: 12, color: "#999", whiteSpace: "pre-wrap" }}>
                          {ev.note}
                        </div>
                      )}
                    </div>

                    {canDelete && (
                      <button
                        className="smallbtn"
                        style={{ background: "#ef4444", color: "#fff" }}
                        onClick={() => handleDelete(ev)}
                      >
                        刪除
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {openCreate && (
        <CreateCalendarEventModal
          onClose={() => setOpenCreate(false)}
          onSuccess={() => setOpenCreate(false)}
        />
      )}
    </div>
  );
}
